import {
    Smartphone, Download, Check, Star, Shield, Heart, Users,
    Home, MapPin, Calendar, MessageCircle, Mail, Phone, Globe, Lock,
    Sparkles, Zap, Gift, Rocket, BookOpen, Camera, Music, Coffee,
    Apple, Chrome, CheckCircle, Share, PlusSquare, MoreVertical
} from 'lucide-react';

/**
 * L'apparence d'un bloc : son icône, sa couleur, son alignement.
 *
 * ========================================================================
 * UN NOM EN BASE, UN COMPOSANT ICI
 * ========================================================================
 *
 * La base stocke « smartphone », jamais du SVG. Stocker du balisage
 * reviendrait à stocker du code que personne ne relit, et qu'aucun filtre ne
 * saurait vérifier — alors qu'un nom inconnu se réduit ici à « pas d'icône ».
 *
 * Le serveur valide déjà ces noms contre sa propre liste (VALEURS_ENUM dans
 * cabaza_pages.cfc). Cette table en est le miroir : un nom ajouté là-bas et
 * oublié ici n'affiche rien, ce qui se voit — l'inverse serait refusé à
 * l'enregistrement, ce qui se voit aussi.
 *
 * ========================================================================
 * POURQUOI DES CLASSES FIGÉES ET NON UNE COULEUR LIBRE
 * ========================================================================
 *
 * Tailwind compile les classes qu'il TROUVE DANS LE CODE : `bg-${couleur}-100`
 * construit à l'exécution ne produirait aucune règle CSS, et la pastille
 * serait transparente. D'où des classes écrites en toutes lettres.
 *
 * C'est aussi ce qui tient la charte : l'éditeur choisit parmi huit couleurs
 * qui vont ensemble, au lieu de saisir un code hexadécimal qui jure.
 */

const ICONES = {
    smartphone: Smartphone,
    download: Download,
    check: Check,
    star: Star,
    shield: Shield,
    heart: Heart,
    users: Users,
    home: Home,
    mappin: MapPin,
    calendar: Calendar,
    message: MessageCircle,
    mail: Mail,
    phone: Phone,
    globe: Globe,
    lock: Lock,
    sparkles: Sparkles,
    zap: Zap,
    gift: Gift,
    rocket: Rocket,
    book: BookOpen,
    camera: Camera,
    music: Music,
    coffee: Coffee,
    // Les clés sont EN MINUSCULES : `iconeDe` abaisse la casse avant de
    // chercher ici, comme le montre `mappin` plus haut.
    apple: Apple,
    chrome: Chrome,
    // Les gestes de la page d'installation : partager, ajouter à l'écran
    // d'accueil, ouvrir le menu, et la réussite. Sans elles, les étapes du
    // rendu par blocs portaient des icônes qui ne montraient pas le bouton
    // à toucher. Cles en minuscules, comme les autres.
    checkcircle: CheckCircle,
    share: Share,
    plussquare: PlusSquare,
    morevertical: MoreVertical
};

/**
 * Le composant d'icône, ou null si aucune n'est choisie.
 *
 * La casse est ignorée : la base stocke en minuscules, mais un contenu
 * importé pourrait porter « mapPin ».
 */
export const iconeDe = (nom) => {
    if (!nom || nom === 'aucune') return null;
    return ICONES[String(nom).toLowerCase()] || null;
};

/**
 * Les classes d'une pastille colorée : fond clair, icône soutenue.
 *
 * `cabaza` est le dégradé de la charte, sur fond plein et icône blanche —
 * c'est la variante mise en avant, celle du bloc principal d'une page.
 */
const COULEURS = {
    cabaza: { pastille: 'bg-gradient-to-br from-cabaza-purple to-cabaza-pink', icone: 'text-white' },
    violet: { pastille: 'bg-purple-100', icone: 'text-purple-600' },
    rose:   { pastille: 'bg-pink-100',   icone: 'text-pink-600' },
    bleu:   { pastille: 'bg-blue-100',   icone: 'text-blue-600' },
    vert:   { pastille: 'bg-green-100',  icone: 'text-green-600' },
    ambre:  { pastille: 'bg-amber-100',  icone: 'text-amber-600' },
    rouge:  { pastille: 'bg-red-100',    icone: 'text-red-600' },
    gris:   { pastille: 'bg-gray-100',   icone: 'text-gray-500' }
};

export const couleurDe = (nom) => COULEURS[String(nom || 'cabaza').toLowerCase()] || COULEURS.cabaza;

/** Les classes d'alignement d'un bloc. */
export const alignementDe = (nom) => (
    String(nom).toLowerCase() === 'centre'
        ? { conteneur: 'text-center', pastille: 'mx-auto', onglets: 'justify-center' }
        : { conteneur: '', pastille: '', onglets: '' }
);

/**
 * La marge sous un bloc.
 *
 * « normal » garde la marge que chaque bloc posait jusqu'ici (`defaut`,
 * propre au bloc) : aucune page existante ne bouge. « large » et
 * « tresLarge » reprennent les écarts de InstallAppPage — mb-12 entre ses
 * sections, mt-16 avant sa FAQ —, qu'aucun bloc ne savait reproduire.
 *
 * Une valeur inconnue retombe sur le défaut du bloc : la valeur venue de la
 * base ne sert jamais à fabriquer une classe, et `defaut` est toujours une
 * classe écrite en toutes lettres par l'appelant.
 */
const ESPACES = {
    large: 'mb-12',
    treslarge: 'mb-16'
};

export const espaceDe = (nom, defaut) => ESPACES[String(nom || '').toLowerCase()] || defaut;

/**
 * Le nombre de colonnes d'une grille.
 *
 * `auto` s'adapte au nombre de cartes : trois cartes tiennent en trois
 * colonnes, quatre en quatre. C'est ce qu'on attend sans y penser, et c'est
 * pourquoi c'est le défaut.
 */
export const colonnesDe = (nom, nbElements) => {
    const choix = String(nom || 'auto').toLowerCase();

    // md: ET NON lg: — le conteneur d'une page de contenu fait 896 px de
    // large (max-w-4xl). Une rangee de trois cartes demandee en `lg:`
    // (1024 px) ne s'y declenchait jamais : la grille restait sur deux
    // colonnes, et la troisieme carte tombait seule a la ligne.
    if (choix === '2') return 'sm:grid-cols-2';
    if (choix === '3') return 'sm:grid-cols-2 md:grid-cols-3';
    if (choix === '4') return 'sm:grid-cols-2 md:grid-cols-4';

    if (nbElements >= 4) return 'sm:grid-cols-2 md:grid-cols-4';
    if (nbElements === 3) return 'sm:grid-cols-2 md:grid-cols-3';
    return 'sm:grid-cols-2';
};

/**
 * La puce d'une étape : son icône si elle en a une, son numéro sinon.
 *
 * LE NUMÉRO RESTE LE DÉFAUT. Une suite d'étapes se lit dans l'ordre, et le
 * rang est l'information la plus utile — l'icône ne vient que l'illustrer.
 * Elle remplace le numéro plutôt que de s'y ajouter : deux marqueurs côte à
 * côte encombrent la colonne sans rien dire de plus.
 *
 * Même pastille dégradée dans les deux cas : la colonne reste alignée qu'une
 * étape porte une icône ou non.
 */
export const Puce = ({ index, icone }) => {
    const Icone = iconeDe(icone);

    return (
        <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white bg-gradient-to-br from-cabaza-purple to-cabaza-pink">
            {Icone ? <Icone className="w-4 h-4" /> : index + 1}
        </span>
    );
};
